// Pipeline impact API client — public endpoints for browsing LCA impacts
// of planned solar/wind generators from the EIA 860M pipeline.

import { API } from "./api";

// ─── Types ───────────────────────────────────────────────────────────

export interface StateImpact {
  state_id: string;
  state_name: string;
  generator_count: number;
  total_capacity_mw: number;
  annual_generation_mwh: number;
  dalys_avoided: number;
  pdfs_avoided: number;
  usd_avoided: number;
  technologies: Record<string, number>;
}

export interface StateImpactsResponse {
  states: StateImpact[];
  totals: StateImpact;
  period: string | null;
  computed_at: string | null;
}

export interface BAImpact {
  ba_code: string;
  generator_count: number;
  total_capacity_mw: number;
  annual_generation_mwh: number;
  dalys_avoided: number;
  pdfs_avoided: number;
  usd_avoided: number;
}

export interface BAImpactsResponse {
  bas: BAImpact[];
  totals: BAImpact | null;
  period: string | null;
}

export interface GeneratorImpact {
  plant_id: string;
  generator_id: string;
  plant_name: string | null;
  entity_name: string | null;
  state_id: string | null;
  ba_code: string | null;
  technology: string | null;
  tech_type_id: string | null;
  nameplate_capacity_mw: number | null;
  annual_generation_mwh: number | null;
  dalys_avoided: number | null;
  pdfs_avoided: number | null;
  usd_avoided: number | null;
}

export interface GeneratorImpactsResponse {
  generators: GeneratorImpact[];
  total: number;
  page: number;
  page_size: number;
}

export interface GeneratorImpactSearchParams {
  state?: string;
  ba_code?: string;
  technology?: string;
  page?: number;
  page_size?: number;
}

// ─── API Functions ───────────────────────────────────────────────────

export async function fetchStateImpacts(): Promise<StateImpactsResponse> {
  const res = await fetch(`${API}/public/pipeline/state-impacts`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function fetchBAImpacts(): Promise<BAImpactsResponse> {
  const res = await fetch(`${API}/public/pipeline/ba-impacts`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function fetchGeneratorImpacts(
  params: GeneratorImpactSearchParams = {}
): Promise<GeneratorImpactsResponse> {
  const qs = new URLSearchParams();
  if (params.state) qs.set("state", params.state);
  if (params.ba_code) qs.set("ba_code", params.ba_code);
  if (params.technology) qs.set("technology", params.technology);
  if (params.page) qs.set("page", String(params.page));
  if (params.page_size) qs.set("page_size", String(params.page_size));

  const query = qs.toString() ? `?${qs.toString()}` : "";
  const res = await fetch(`${API}/public/pipeline/generators${query}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function fetchGeneratorImpact(
  plantId: string,
  generatorId: string
): Promise<GeneratorImpact> {
  const res = await fetch(
    `${API}/public/pipeline/generator/${encodeURIComponent(plantId)}/${encodeURIComponent(generatorId)}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
